import { profileOverride } from 'modules/auth/auth-client';
import { LoggedUser, MOCK_ACCOUNTS, MockAccount, MockUserRole } from 'pages/auth/mock-auth.const';
import { ROLE } from 'services/role/role.model';
import { CurrentUser } from 'services/user/user.model';
import { MenuClient } from 'services/navigation/navigation.model';
import { mockRoute, MockHttpError } from '../mock-api';
import {
	buildMenuClient,
	buildMenuProduct,
	buildMenuVertical,
	isHubProduct,
	MOCK_CONTEXT_CLIENT,
	MOCK_HUB_ROLES,
	HubProductSlug,
} from '../shared/mock-context.const';
import { MOCK_CLIENTS, PROTOTYPE_CLIENT_IDS } from '../shared/prototype-clients.const';

/** Mesma chave usada pelo MockAuthService para guardar a persona logada */
const LOGGED_USER_KEY = 'hub_prototype_user';

const PERSONA_ROLE: Record<MockUserRole, ROLE> = {
	admin_master: ROLE.BUSINESS_USER,
	gerente: ROLE.BUSINESS_USER,
	operator: ROLE.OPERATOR,
	validador: ROLE.OPERATOR,
};

let activeProductSetupId: string | null = null;

/**
 * Outras empresas do seletor de contexto (admin e gerente), com produtos e verticais diferentes
 * dos da Acme. Os setups existem em `mocks/context` e os jobs em `mocks/shared/mock-jobs-roster.const.ts`.
 */
const OTHER_CONTEXT_CLIENTS: {
	slug: keyof typeof PROTOTYPE_CLIENT_IDS;
	verticals: Record<string, HubProductSlug[]>;
}[] = [
	{ slug: 'aurora', verticals: { civel: ['cadastro'], trabalhista: ['contestacao'] } },
	{ slug: 'pampa', verticals: { civel: ['basilisk'], trabalhista: ['cadastro'] } },
];

function otherClientMenu(
	slug: keyof typeof PROTOTYPE_CLIENT_IDS,
	verticals: Record<string, HubProductSlug[]>,
	role: ROLE,
): MenuClient | null {
	const client = MOCK_CLIENTS.find((candidate) => candidate.id === PROTOTYPE_CLIENT_IDS[slug]);
	if (!client) return null;
	return buildMenuClient(
		Object.entries(verticals).map(([verticalSlug, products]) =>
			buildMenuVertical(
				verticalSlug,
				products.map((product) =>
					buildMenuProduct(verticalSlug, product, role, `ps-${slug}-${verticalSlug}-${product}`),
				),
			),
		),
		{
			id: client.id,
			name: client.name,
			cnpj: client.cnpj,
			logo: client.logo ?? null,
			members: client.members,
		},
	);
}

function loggedUser(): LoggedUser | null {
	try {
		const raw = localStorage.getItem(LOGGED_USER_KEY);
		return raw ? (JSON.parse(raw) as LoggedUser) : null;
	} catch {
		return null;
	}
}

/** Monta o `CurrentUser` do Hub (GET /session/whoami) a partir da persona logada */
export function buildCurrentUser(): CurrentUser {
	const user = loggedUser();
	const account = MOCK_ACCOUNTS.find((candidate) => candidate.id === user?.id);
	if (!account) throw new MockHttpError(401, 'Não autenticado');

	return currentUserOf(account);
}

/** `CurrentUser` do Hub para uma persona do protótipo */
export function currentUserOf(account: MockAccount): CurrentUser {
	const role = PERSONA_ROLE[account.role];
	const verticals = Object.entries(account.access.verticals ?? {})
		.map(([verticalSlug, products]) =>
			buildMenuVertical(
				verticalSlug,
				products.filter(isHubProduct).map((slug) => buildMenuProduct(verticalSlug, slug, role)),
			),
		)
		.filter((vertical) => vertical.products.length > 0);

	const hasOtherClients = account.role === 'admin_master' || account.role === 'gerente';
	const menu: MenuClient[] = [
		buildMenuClient(verticals),
		...(hasOtherClients
			? OTHER_CONTEXT_CLIENTS.map((other) => otherClientMenu(other.slug, other.verticals, role))
			: []
		).filter((client): client is MenuClient => client !== null),
	];

	// Troca de persona: o contexto ativo precisa pertencer ao novo usuário
	const productSetupIds = menu.flatMap((client) =>
		client.verticals.flatMap((vertical) =>
			vertical.products.map((product) => product.productSetupId),
		),
	);
	if (!activeProductSetupId || !productSetupIds.includes(activeProductSetupId)) {
		activeProductSetupId = productSetupIds[0] ?? null;
	}

	return {
		id: account.id,
		email: account.email,
		// Nome/foto editados em Configurações › Perfil (auth-client mock)
		name: profileOverride(account.id).name ?? account.name,
		emailVerified: true,
		image: profileOverride(account.id).image ?? null,
		productSetupId: activeProductSetupId ?? '',
		roleId: MOCK_HUB_ROLES[role].id,
		clientId: MOCK_CONTEXT_CLIENT.id,
		verticalId: verticals[0]?.id ?? '',
		isAdmin: account.role === 'admin_master',
		menu,
	};
}

export const SESSION_MOCK_ROUTES = [
	mockRoute('GET', '/session/whoami', () => buildCurrentUser()),
	mockRoute<{ productSetupId: string }>('POST', '/session/switch-context', ({ body }) => {
		activeProductSetupId = body.productSetupId;
		return { productSetupId: body.productSetupId, roleId: buildCurrentUser().roleId };
	}),
];
